import { useState } from 'react';
import { AsyncBoundary, Card, Notice, useToast } from '@kollez/design-system';
import {
  useRecentAutoApprovedMemberships,
  useRevokeMembership,
} from '@/lib/queries';
import { formatIndianDate } from '@kollez/shared';

/**
 * /tpo/memberships/auto-approved — spot-audit surface for Tier 2a email-domain
 * auto-approvals over the last 30 days.
 *
 * Every auto-approve fires a Notification to the TPO. The consolidated queue lives
 * at GET /tpo/memberships/recently-auto-approved and revoke fires POST
 * /tpo/memberships/:id/revoke with a typed reason. Both endpoints existed but
 * had no page. The revoke flow demotes primary + writes AuditLog + notifies the
 * student.
 */

export function TpoAutoApprovedMembershipsPage() {
  const [q, setQ] = useState('');
  const list = useRecentAutoApprovedMemberships({ q: q.trim() || undefined, limit: 200 });
  const revoke = useRevokeMembership();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const rows = list.data?.candidates ?? [];
  const windowDays = list.data?.windowDays ?? 30;

  async function doRevoke(m: { membershipId: string; fullName?: string; email?: string }) {
    const note = window.prompt(`Revoke ${m.fullName ?? m.email ?? 'this membership'}?\n\nOptional note (visible in AuditLog):`);
    if (note === null) return;
    setBusyId(m.membershipId);
    try {
      const res = await revoke.mutateAsync({
        membershipId: m.membershipId,
        reason: 'bad_auto_approval',
        note: note.trim() || undefined,
      });
      toast.show({
        message: res.alreadyEnded ? 'Already ended.' : `Revoked ${m.fullName ?? m.email ?? 'membership'}.`,
      });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Revoke failed.' });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Memberships · Auto-approved</p>
          <h1 className="h-page">Recent email-domain auto-approvals</h1>
          <p className="subhead">
            Students verified via their college email domain in the last {windowDays} days.
            Revoke anything that looks wrong — the student gets a notification and the AuditLog
            records the reason.
          </p>
        </div>
      </div>

      <Notice tone="info">
        Kollez auto-approves memberships when a student's email matches your tenant's registered
        domain (Tier 2a). This queue is your spot-audit. Everything here is already active on your
        roster.
      </Notice>

      <Card padding="md">
        <label className="flex flex-col text-sm">
          <span className="cell-sub">Search by name or email</span>
          <input
            className="input mt-1"
            placeholder="e.g. rahul, @git.edu.in"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </Card>

      <Card padding="none">
        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle={q ? 'No matches' : 'No auto-approvals in the last 30 days'}
          emptyHint={q
            ? 'Try a different search term or clear the filter.'
            : 'When a student signs up with an email that matches your domain, they land here.'}
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Student</th>
                  <th className="hide-mobile">Email</th>
                  <th className="hide-mobile">Relationship</th>
                  <th>Verified</th>
                  <th className="r">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.membershipId}>
                    <td>
                      <div className="cell-title">{m.fullName ?? '—'}</div>
                      {m.phone && <div className="cell-sub">{m.phone}</div>}
                    </td>
                    <td className="hide-mobile cell-sub">{m.email ?? '—'}</td>
                    <td className="hide-mobile">
                      {m.relationship ? <span className="badge">{m.relationship}</span> : '—'}
                    </td>
                    <td className="cell-sub">
                      {m.verifiedAt ? formatIndianDate(new Date(m.verifiedAt)) : '—'}
                    </td>
                    <td className="r">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={busyId === m.membershipId}
                        onClick={() => void doRevoke(m)}
                      >
                        {busyId === m.membershipId ? 'Revoking…' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </Card>
    </div>
  );
}
